import { Box, Flex, HStack, Stack, StackDivider, Text, TextProps } from '@chakra-ui/react'
import {
  FieldTypes,
  formatPhoneNumber,
  getDateString,
  getPregnancyPrimaryCoverage,
  getYearsSinceString,
  planDesignOptions,
  stateAcronyms,
  TextField,
} from '@hb/shared'

import { useApp, useFormattedValue } from '@hb/react'
import { useMemo } from 'react'
import { useProfile } from '../../../../../contexts/user/ProfileContext'
import {
  CoverageTerminationStatus,
  useTerminationStatus,
} from '../../../../Assessments/Coverage/TerminationStatus'
import { CopyTooltip } from '../../../../CopyTooltip'
import { AssessmentClearedSection } from '../../Assessments/AssessmentClearedSection'
import { PregnancyStatusDropdowns } from '../../Assessments/PregnancyStatus'
import { AssessmentThreadView } from '../../AssessmentThread'

const HeaderItem = (props: TextProps) => (
  <Flex px={1} whiteSpace="nowrap" fontSize="sm" color="#333" {...props} />
)

const SecondaryCoverageSummary = () => {
  const {
    selectedAssessment: { populated },
  } = useProfile()
  const { plans } = populated ?? {}
  const { secondary } = plans ?? {}

  const secondaryCoverageTerminationStatus = useTerminationStatus(plans?.secondary)
  return secondary?.insuranceProviderId ? (
    <Flex gap={1.5} align="center">
      <Box>
        <HeaderItem>
          <span style={{ marginRight: '4px', fontWeight: 600 }}>
            Secondary Coverage{secondary.isMedicaid ? ' (Medicaid)' : ''}
          </span>
          {secondary?.insuranceProvider?.name || 'No insurer'}
          {/* {midwife ? AdminPracticeData?.taxId?.main ?? 'None' : 'N/A'} */}
        </HeaderItem>
        <HeaderItem>
          <span style={{ marginRight: '4px', fontWeight: 600 }}>Secondary Coverage ID</span>
          {secondary.memberId || 'None'}
        </HeaderItem>
      </Box>
      <CoverageTerminationStatus
        wrap
        simple
        assessment={populated}
        coverage={secondary}
        terminationStatus={secondaryCoverageTerminationStatus}
      />
    </Flex>
  ) : null
}

const midwifeTaxIdField: TextField = {
  type: FieldTypes.TEXT,
  placeholder: 'Midwife Tax ID',
}
const midwifeNPIField: TextField = {
  type: FieldTypes.TEXT,
  placeholder: 'Midwife NPI',
}

export const AssessmentHeaderRow = ({ width }: { width: number }) => {
  const {
    selectedAssessment: { populated: selectedAssessment },
    user,
  } = useProfile()
  const { midwife, deliveredOn, edd, planState, plans } = selectedAssessment ?? {}
  const { secondary } = plans ?? {}
  const { dob } = user ?? {}

  const { appName } = useApp()
  const primary = useMemo(() => getPregnancyPrimaryCoverage(plans), [plans])
  const {
    memberId,
    insuranceProviderNumber,
    'plan-design-and-state-mandates': planDesign,
  } = primary ?? {}
  const planDesignText = useMemo(() => {
    if (!planDesign?.planDesign) return ''
    return planDesignOptions.find(p => p.id === planDesign.planDesign)?.text ?? ''
  }, [planDesign])

  const weeksIn = useMemo(() => {
    if (deliveredOn === Infinity) return undefined
    if (edd) {
      const delivered = deliveredOn && deliveredOn > 0 ? deliveredOn : Date.now()
      // start date is 40 weeks before edd
      const startDate = edd - 1000 * 60 * 60 * 24 * 7 * 40
      return Math.floor((delivered - startDate) / (1000 * 60 * 60 * 24 * 7))
    }
    return undefined
  }, [edd, deliveredOn])

  const { name: insuranceProviderName } = primary?.insuranceProvider ?? {}
  const { value: midwifeTaxId } = useFormattedValue(midwifeTaxIdField, midwife?.taxId)
  const { value: midwifeNPI } = useFormattedValue(midwifeNPIField, midwife?.npi)

  const coveragePrefix = useMemo(() => {
    let text = planDesignText ?? ''
    if (planState) text += `${text ? ' ' : ''}(${stateAcronyms[planState]})`
    return text
  }, [planDesignText, planState])
  const coverageText = useMemo(
    () => `${memberId || 'None'}${coveragePrefix ? ` - ${coveragePrefix}` : ''}`,
    [memberId, coveragePrefix],
  )

  const isMobile = useMemo(() => width < 800, [width])

  const primaryCoverageTerminationStatus = useTerminationStatus(primary)

  return (
    <Flex
      // borderBottom="1px solid #cdcdcd"
      boxShadow="inset 0px -1px 1px #cdcdcd"
      // align='center'
      flexFlow={isMobile ? 'column' : 'row wrap'}
      bg="gray.50"
      width="100%"
      maxW="100%"
      align="center">
      <Stack
        px={2}
        py={1}
        flex={isMobile ? 'unset' : 1}
        spacing={1}
        // borderBottom={isMobile ? '1px solid #cdcdcd' : 'none'}
        borderBottom="1px solid #cdcdcd"
        direction={['column', 'column', 'row']}
        w={isMobile ? '100%' : 'auto'}
        divider={<StackDivider borderColor="blackAlpha.500" />}>
        <Flex w={isMobile ? '100%' : 'auto'}>
          {/* On mobile the two dates share one row (wrapping if needed) to save vertical space */}
          <Flex
            flexFlow={isMobile ? 'row' : 'column'}
            flexWrap={isMobile ? 'wrap' : 'nowrap'}
            columnGap={isMobile ? 3 : 0}
            flex={isMobile ? 1 : 'unset'}
            minW="0"
            w={isMobile ? 'unset' : 'auto'}>
            <HeaderItem fontSize={isMobile ? 'xs' : 'sm'}>
              <span style={{ marginRight: '4px', fontWeight: 600 }}>DOB</span>
              <CopyTooltip label={dob ? getDateString(dob, 'short') : 'None'}>
                {dob
                  ? `${getDateString(dob, 'short')} (${getYearsSinceString(dob)} years)`
                  : 'None'}
              </CopyTooltip>
            </HeaderItem>
            {deliveredOn ? (
              <HeaderItem fontSize={isMobile ? 'xs' : 'sm'}>
                <span style={{ fontWeight: 600, marginRight: '4px' }}>Delivered</span>
                <CopyTooltip label={getDateString(deliveredOn, 'short')}>
                  <Text>
                    {deliveredOn === Infinity ? 'Transfer' : getDateString(deliveredOn, 'short')}
                    {weeksIn ? ` - ${weeksIn} weeks` : ''}
                  </Text>
                </CopyTooltip>
              </HeaderItem>
            ) : (
              <HeaderItem fontSize={isMobile ? 'xs' : 'sm'}>
                <span style={{ fontWeight: 600, marginRight: '4px' }}>EDD</span>
                <CopyTooltip label={edd ? getDateString(edd, 'short') : 'None'}>
                  <Text>{edd ? `${getDateString(edd, 'short')} (${weeksIn} weeks)` : 'None'}</Text>
                </CopyTooltip>
              </HeaderItem>
            )}
          </Flex>
        </Flex>
        {appName === 'app' ? (
          <Box overflow="hidden" maxW="30%" flex={1} minW="0">
            <HeaderItem width="100%">
              <CopyTooltip
                multiLine
                prefix={coveragePrefix}
                label={memberId ?? ''}
                suffix="Member ID">
                <Flex w="100%">
                  <Text mr={1} isTruncated fontWeight={600}>
                    {`${insuranceProviderName || 'Member'} ID `}
                  </Text>
                  <Text flex={1} minW="0" isTruncated>
                    {coverageText}
                  </Text>
                </Flex>
              </CopyTooltip>
            </HeaderItem>
            <HeaderItem>
              <span style={{ marginRight: '4px', fontWeight: 600 }}>
                {insuranceProviderName || 'Insurance Provider '} Phone
              </span>
              <CopyTooltip
                label={
                  insuranceProviderNumber ? formatPhoneNumber(insuranceProviderNumber) : 'None'
                }>
                <Text>
                  {insuranceProviderNumber ? formatPhoneNumber(insuranceProviderNumber) : 'None'}
                </Text>
              </CopyTooltip>
            </HeaderItem>
          </Box>
        ) : null}
        {appName === 'app' ? (
          <Box>
            <HeaderItem>
              <span style={{ marginRight: '4px', fontWeight: 600 }}>Midwife Tax ID</span>
              <CopyTooltip label={midwifeTaxId}>
                <Text>{midwifeTaxId}</Text>
              </CopyTooltip>
              {/* {midwife ? AdminPracticeData?.taxId?.main ?? 'None' : 'N/A'} */}
            </HeaderItem>
            <HeaderItem>
              <span style={{ marginRight: '4px', fontWeight: 600 }}>Midwife NPI</span>
              <CopyTooltip label={midwifeNPI}>
                <Text>{midwifeNPI}</Text>
              </CopyTooltip>
              {/* {midwife ? AdminPracticeData?.taxId?.main ?? 'None' : 'N/A'} */}
            </HeaderItem>
          </Box>
        ) : null}
        {secondary?.insuranceProviderId ? <SecondaryCoverageSummary /> : null}
        {primaryCoverageTerminationStatus ? (
          <Box>
            <HeaderItem>
              <Flex gap={1} align="center">
                <CoverageTerminationStatus
                  simple
                  wrap
                  assessment={selectedAssessment}
                  coverage={primary}
                  terminationStatus={primaryCoverageTerminationStatus}
                />
              </Flex>
            </HeaderItem>
          </Box>
        ) : null}
      </Stack>
      <HStack
        borderTop={isMobile ? '1px solid #cdcdcd' : 'none'}
        justify="flex-end"
        minW="0"
        ml={isMobile ? 'unset' : 'auto'}
        w={isMobile ? '100%' : 'auto'}
        // flex={1}
        px={2}
        py={1}
        spacing={3}>
        {/* {isMobile ? null : <ArchivedByOtherPreview />} */}
        <Flex gap={2} mr={isMobile ? 'auto' : 0} minW="0">
          <AssessmentThreadView mobileView={isMobile} />
        </Flex>
        <Box>
          <AssessmentClearedSection mobileView={isMobile} />
        </Box>
        {/* {isMobile ? null : <PregnancyStatusDropdowns />} */}
        <PregnancyStatusDropdowns isMobile={isMobile} />
      </HStack>
    </Flex>
  )
}
